/* ============================================================
   SAM-198 — editor v2 (mock 3 v2) building blocks for "Booking & hours":
   the booking window as a sentence with units, the custom-hours editor
   (per-day switch, ranges, + Add hours, Copy Monday) and the weekly bars.

   Presentation only; the conversions live in ../editorV2.ts and the form
   state in useServiceEditor. Tailwind + tokens (SAM-175 freeze).
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { Copy, Plus, X } from 'lucide-react';
import { DAYS_OF_WEEK, type OperatingHourForm } from '../../../hooks/useServiceEditor';
import type { OperatingHours } from '../../../hooks/useServices';
import {
  aheadToParts, leadToParts, partsToAheadDays, partsToLeadHours, describeBookingWindow, weekBars,
  type AheadUnit, type LeadUnit,
} from '../editorV2';

const numInput = 'h-10 w-20 rounded-[8px] border border-solid border-[var(--line)] bg-white px-2 text-[14px]';
const unitSelect = 'h-10 rounded-[8px] border border-solid border-[var(--line)] bg-white px-2 text-[14px]';

/** Number + unit kept locally so "14 days" does not jump to "2 weeks" while typing; re-synced
 * when the stored value changes from outside (reset, load). */
function useParts<U extends string>(
  stored: string,
  toParts: (v: string) => { value: string; unit: U },
  fromParts: (value: string, unit: U) => string,
) {
  const [parts, setParts] = useState(() => toParts(stored));
  useEffect(() => {
    if (fromParts(parts.value, parts.unit) !== stored) setParts(toParts(stored));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored]);
  return [parts, setParts] as const;
}

export function BookingWindowSentence({
  aheadDays, leadHours, onAheadDays, onLeadHours, perTime,
}: {
  aheadDays: string;
  leadHours: string;
  onAheadDays: (days: string) => void;
  onLeadHours: (hours: string) => void;
  /** Fixed/flexible services close per start time; day services per date. */
  perTime: boolean;
}) {
  const [ahead, setAhead] = useParts<AheadUnit>(aheadDays, aheadToParts, partsToAheadDays);
  const [lead, setLead] = useParts<LeadUnit>(leadHours, leadToParts, partsToLeadHours);
  const changeAhead = (value: string, unit: AheadUnit) => {
    setAhead({ value, unit });
    onAheadDays(partsToAheadDays(value, unit));
  };
  const changeLead = (value: string, unit: LeadUnit) => {
    setLead({ value, unit });
    onLeadHours(partsToLeadHours(value, unit));
  };
  return (
    <div>
      <p className="flex flex-wrap items-center gap-2 text-[15px]" style={{ color: 'var(--ink-900)' }}>
        <span>From</span>
        <input
          type="number" min={0} aria-label="How far ahead" className={numInput}
          value={ahead.value} placeholder="30" onChange={(e) => changeAhead(e.target.value, ahead.unit)}
        />
        <select aria-label="How far ahead unit" className={unitSelect} value={ahead.unit} onChange={(e) => changeAhead(ahead.value, e.target.value as AheadUnit)}>
          <option value="days">days</option>
          <option value="weeks">weeks</option>
        </select>
        <span>ahead, until</span>
        <input
          type="number" min={0} aria-label="Closes before" className={numInput}
          value={lead.value} placeholder="0" onChange={(e) => changeLead(e.target.value, lead.unit)}
        />
        <select aria-label="Closes before unit" className={unitSelect} value={lead.unit} onChange={(e) => changeLead(lead.value, e.target.value as LeadUnit)}>
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
        <span>before {perTime ? 'the start time' : 'the day opens'}.</span>
      </p>
      <p className="text-[12px] mt-2" style={{ color: 'var(--ink-500)' }}>
        {describeBookingWindow(aheadDays, leadHours, new Date())}
      </p>
    </div>
  );
}

function Switch({ on, onChange, label }: { on: boolean; onChange: (on: boolean) => void; label: string }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}
      className="relative shrink-0 rounded-full border-0 cursor-pointer"
      style={{ width: 40, height: 24, background: on ? 'var(--tenant-primary, #005488)' : 'var(--ink-300, #c4c4c4)' }}
    >
      <span
        aria-hidden
        className="absolute top-[3px] rounded-full bg-white transition-all"
        style={{ width: 18, height: 18, left: on ? 19 : 3, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
      />
    </button>
  );
}

const timeInput = 'h-10 rounded-[8px] border border-solid border-[var(--line)] bg-white px-2 text-[14px] w-[112px]';

export function DayHoursEditor({
  hours, errors, onToggle, onFirstChange, onRemoveFirst, onAdd, onExtraChange, onRemoveExtra, onCopyMonday,
}: {
  hours: Record<string, OperatingHourForm>;
  errors: Record<string, string[]>;
  onToggle: (day: string, on: boolean) => void;
  onFirstChange: (day: string, field: 'open' | 'close', value: string) => void;
  onRemoveFirst: (day: string) => void;
  onAdd: (day: string) => void;
  onExtraChange: (day: string, index: number, field: 'open' | 'close', value: string) => void;
  onRemoveExtra: (day: string, index: number) => void;
  onCopyMonday: () => void;
}) {
  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          type="button" onClick={onCopyMonday}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-white text-[13px] font-semibold cursor-pointer"
          style={{ color: 'var(--ink-700)' }}
        >
          <Copy size={13} /> Copy Monday to all days
        </button>
      </div>
      <div className="flex flex-col">
        {DAYS_OF_WEEK.map(({ key, label }) => {
          const day = hours[key];
          const on = !!day?.enabled;
          const dayErrors = errors[key] || [];
          const short = label.charAt(0) + label.slice(1).toLowerCase();
          return (
            <div key={key} className="flex items-start gap-3 py-2.5" style={{ borderTop: '1px solid var(--line-subtle)' }} data-testid={`hours-${key}`}>
              <div className="flex items-center gap-2.5 pt-2 shrink-0" style={{ width: 92 }}>
                <Switch on={on} onChange={(v) => onToggle(key, v)} label={`Open on ${short}`} />
                <span className="text-[13px] font-semibold" style={{ color: 'var(--ink-700)' }}>{short}</span>
              </div>
              {!on ? (
                <span className="text-[13px] pt-2.5" style={{ color: 'var(--ink-400)' }}>Closed</span>
              ) : (
                <div className="flex flex-col gap-1.5 min-w-0">
                  {[{ open: day.open, close: day.close }, ...day.extra_windows].map((w, i) => (
                    <div key={i} className="flex items-center gap-1.5 flex-wrap">
                      <input
                        type="time" className={timeInput} aria-label={`${short} range ${i + 1} opens`} value={w.open}
                        onChange={(e) => (i === 0 ? onFirstChange(key, 'open', e.target.value) : onExtraChange(key, i - 1, 'open', e.target.value))}
                      />
                      <span className="text-[12px]" style={{ color: 'var(--ink-400)' }}>–</span>
                      <input
                        type="time" className={timeInput} aria-label={`${short} range ${i + 1} closes`} value={w.close}
                        onChange={(e) => (i === 0 ? onFirstChange(key, 'close', e.target.value) : onExtraChange(key, i - 1, 'close', e.target.value))}
                      />
                      <button
                        type="button" aria-label={`Remove ${short} range ${i + 1}`}
                        onClick={() => (i === 0 ? onRemoveFirst(key) : onRemoveExtra(key, i - 1))}
                        className="w-9 h-9 inline-flex items-center justify-center rounded-[8px] border-0 bg-transparent cursor-pointer"
                        style={{ color: 'var(--ink-400)' }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button" onClick={() => onAdd(key)}
                    className="self-start inline-flex items-center gap-1 h-8 px-0 border-0 bg-transparent text-[13px] font-semibold cursor-pointer"
                    style={{ color: 'var(--tenant-primary, #005488)' }}
                  >
                    <Plus size={13} /> Add hours
                  </button>
                  {dayErrors.map((msg) => (
                    <p key={msg} className="text-[11px]" style={{ color: 'var(--danger)' }}>{msg}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** "This week in the app": the bookable ranges per day, with the business's hours behind them. */
export function WeekBarsChart({ hours, businessHours }: { hours: OperatingHours; businessHours?: OperatingHours }) {
  const bars = weekBars(hours);
  const behind = businessHours ? weekBars(businessHours) : null;
  const pct = (m: number) => `${(m / 1440) * 100}%`;
  return (
    <div className="rounded-[12px] p-4 bg-white" style={{ border: '1px solid var(--line)' }} data-testid="week-bars">
      <div className="text-[13px] font-bold mb-3" style={{ color: 'var(--ink-900)' }}>This week in the app</div>
      <div className="flex justify-between text-[10px] mb-1 pl-10" style={{ color: 'var(--ink-400)' }}>
        <span>12 AM</span><span>6 AM</span><span>12 PM</span><span>6 PM</span><span>12 AM</span>
      </div>
      <div className="flex flex-col gap-1.5">
        {bars.map((d, i) => (
          <div key={d.key} className="flex items-center gap-2">
            <span className="text-[11px] font-semibold w-8 shrink-0" style={{ color: 'var(--ink-700)' }}>{d.short}</span>
            <div className="relative flex-1 h-4 rounded-[4px]" style={{ background: 'var(--line-subtle)' }}>
              {behind?.[i].ranges.map(([a, b]) => (
                <span key={`b${a}`} className="absolute top-0 h-full rounded-[4px]" style={{ left: pct(a), width: pct(b - a), background: 'var(--ink-300, #c4c4c4)', opacity: 0.45 }} />
              ))}
              {d.ranges.map(([a, b]) => (
                <span key={a} className="absolute top-[3px] h-[10px] rounded-[3px]" style={{ left: pct(a), width: pct(b - a), background: 'var(--tenant-primary, #005488)' }} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-3 text-[11px]" style={{ color: 'var(--ink-500)' }}>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-2 rounded-[2px]" style={{ background: 'var(--tenant-primary, #005488)' }} />Bookable</span>
        {behind && <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-2 rounded-[2px]" style={{ background: 'var(--ink-300, #c4c4c4)', opacity: 0.6 }} />Business open</span>}
      </div>
    </div>
  );
}
